import './training.css'

import {
	IonButton,
	IonDatetime,
	IonIcon,
	IonLabel,
	IonModal,
	IonRouterLink,
	IonSegment,
	IonSegmentButton,
	useIonAlert,
} from '@ionic/react'
import { analyticsOutline, arrowBackSharp, arrowForwardSharp, calendarSharp, happyOutline, timeOutline } from 'ionicons/icons'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
	useCanEditItem,
	useCanView,
	useDeleteTrainingSession,
	useMyTrainingNote,
	useMyRegularPlayers,
	useMyTrainingSessions,
	useNoTeamSelected,
	useSaveTrainingNote,
	useSaveTrainingSession,
} from './data'
import LoadingSpinner from './LoadingSpinner'
import NoTeamSelected from './NoTeamSelected'
import PlayerName from './PlayerName'
import { training } from './media/trainingIcon'
import FadeText from './FadeText'
import TrainingNotes from './TrainingNotes'
import TimeAgo from 'react-timeago'

const TRAINING_STATE_LABELS: Record<TrainingAttendanceState, string> = {
	attended: 'Attended',
	part: 'Part',
	missed: 'Missed',
}

const TRAINING_STATE_ICONS: Record<TrainingAttendanceState, string> = {
	attended: happyOutline,
	part: timeOutline,
	missed: happyOutline, 		// We'll actually show no icon, but keep the same dimensions
}

const NEXT_TRAINING_STATE: Record<TrainingAttendanceState, TrainingAttendanceState> = {
	attended: 'part',
	part: 'missed',
	missed: 'attended',
}

const Training = () => {
	const canView = useCanView()
	const canEdit = useCanEditItem()
	const players = useMyRegularPlayers()
	const sessions = useMyTrainingSessions()
	const saveTrainingSession = useSaveTrainingSession()
	const deleteTrainingSession = useDeleteTrainingSession()
	const trainingNote = useMyTrainingNote()
	const saveTrainingNote = useSaveTrainingNote()
	const noTeamSelected = useNoTeamSelected()
	const [ presentAlert ] = useIonAlert()

	const [ selectedDate, setSelectedDate ] = useState<Date>(() => new Date())
	const [ localAttendance, setLocalAttendance ] = useState<Record<PlayerId, TrainingAttendanceState>>({ })
	const [ isTrainingDayOverride, setIsTrainingDayOverride ] = useState(false)
	const [ isDatePickerOpen, setIsDatePickerOpen ] = useState(false)
	const [ segment, setSegment ] = useState<'training' | 'notes'>('training')
	const [ noteDraft, setNoteDraft ] = useState('')
	const [ noteDirty, setNoteDirty ] = useState(false)
	const noteLastSaved = useRef('')

	const NOTE_CHAR_LIMIT = 15000

	const selectedDateId = toDateId(selectedDate)
	const selectedDateMs = toDayStartMs(selectedDate)
	const session = sessions?.[selectedDateId]

	useEffect(() => {
		setLocalAttendance(session?.attendance || { })
	}, [ session?.attendance, selectedDateId ])

	useEffect(() => {
		setIsTrainingDayOverride(false)
	}, [ selectedDateId ])

	const sortedPlayerIds = useMemo(() => {
		if (!players) {
			return [ ]
		}
		return Object.entries(players)
			.sort(([ , valueA ], [ , valueB ]) => valueA.name ? valueA.name.localeCompare(valueB.name) : -1)
			.map(([ key ]) => key)
	}, [ players ])

	const sessionDates = useMemo(() => {
		if (!sessions) {
			return [ ]
		}
		const dates = Object.values(sessions).map(session => {
			const fallbackDate = session.id ? fromDateId(session.id) : new Date()
			return toDayStartMs(new Date(session.date || fallbackDate.getTime()))
		})
		return Array.from(new Set(dates)).sort((a, b) => a - b)
	}, [ sessions ])

	const highlightedDates = useMemo(() => sessionDates.map(dateMs => ({
		date: toDateId(new Date(dateMs)),
		textColor: '#1d4b1d',
		backgroundColor: '#d8f0d8',
	})), [ sessionDates ])

	const todayStartMs = useMemo(() => toDayStartMs(new Date()), [ ])

	const prevDateMs = useMemo(() => {
		const candidates: number[] = [ ]
		if (todayStartMs < selectedDateMs) {
			candidates.push(todayStartMs)
		}
		for (const dateMs of sessionDates) {
			if (dateMs < selectedDateMs) {
				candidates.push(dateMs)
			}
		}
		if (!candidates.length) {
			return undefined
		}
		return Math.max(...candidates)
	}, [ sessionDates, selectedDateMs, todayStartMs ])

	const nextDateMs = useMemo(() => {
		const candidates = sessionDates.filter(dateMs => dateMs > selectedDateMs)
		if (todayStartMs > selectedDateMs) {
			candidates.push(todayStartMs)
		}
		if (!candidates.length) {
			return undefined
		}
		return Math.min(...candidates)
	}, [ sessionDates, selectedDateMs, todayStartMs ])

	const trainingQuestionPrefix = selectedDateMs < todayStartMs
		? 'Was there'
		: selectedDateMs > todayStartMs
			? 'Will there be'
			: 'Is there'

	const trainingQuestionDateLabel = selectedDateMs === todayStartMs ? 'today' : `on ${formatDateLabel(selectedDate)}`
	const isTrainingDay = !!session || isTrainingDayOverride

	useEffect(() => {
		if (!noteDirty) {
			const text = trainingNote?.text || ''
			setNoteDraft(text)
			noteLastSaved.current = text
		}
	}, [ trainingNote?.text, noteDirty ])

	const commitNoteSave = useCallback(async () => {
		if (!canEdit) {
			return
		}
		if (noteDraft === noteLastSaved.current) {
			setNoteDirty(false)
			return
		}
		await saveTrainingNote(noteDraft)
		noteLastSaved.current = noteDraft
		setNoteDirty(false)
	}, [ canEdit, noteDraft, saveTrainingNote ])

	useEffect(() => {
		if (!noteDirty || segment !== 'notes') {
			return
		}
		const timer = setTimeout(() => {
			commitNoteSave()
		}, 3000)
		return () => clearTimeout(timer)
	}, [ noteDirty, segment, noteDraft, commitNoteSave ])

	const onNoteInput = (value: string) => {
		const trimmed = value.length > NOTE_CHAR_LIMIT ? value.slice(0, NOTE_CHAR_LIMIT) : value
		setNoteDraft(trimmed)
		setNoteDirty(true)
	}

	const attendanceStates = useMemo(() => sortedPlayerIds.map(playerId => localAttendance[playerId] || 'missed'), [ localAttendance, sortedPlayerIds ])
	const attendedCount = useMemo(() => attendanceStates.filter(state => state === 'attended' || state === 'part').length, [ attendanceStates ])
	const attendedIcons = useMemo(() => attendanceStates.filter(state => state === 'attended'), [ attendanceStates ])
	const partIcons = useMemo(() => attendanceStates.filter(state => state === 'part'), [ attendanceStates ])

	const updateAttendance = (updated: Record<PlayerId, TrainingAttendanceState>) => {
		setLocalAttendance(updated)
		saveTrainingSession(selectedDateId, selectedDateMs, updated)
	}

	const onCycleAttendance = (playerId: PlayerId) => {
		if (!canEdit) {
			return
		}
		const current = localAttendance[playerId] || 'missed'
		const next = NEXT_TRAINING_STATE[current]
		const updated = {
			...localAttendance,
			[playerId]: next,
		}
		updateAttendance(updated)
	}

	const applyToggleAll = () => {
		if (!canEdit) {
			return
		}
		const shouldMarkAttended = sortedPlayerIds.some(playerId => (localAttendance[playerId] || 'missed') === 'missed')
		const targetState: TrainingAttendanceState = shouldMarkAttended ? 'attended' : 'missed'
		const updated = { ...localAttendance }
		sortedPlayerIds.forEach(playerId => {
			updated[playerId] = targetState
		})
		updateAttendance(updated)
	}

	const onToggleAll = () => {
		if (!canEdit) {
			return
		}
		const totalPlayers = sortedPlayerIds.length
		const isMixed = totalPlayers > 0 && attendedCount > 0 && attendedCount < totalPlayers
		if (!isMixed) {
			applyToggleAll()
			return
		}
		presentAlert({
			header: 'Change everyone?',
			message: 'This will change attendance for all players for this session.',
			buttons: [
				{
					text: 'Cancel',
					role: 'cancel',
				},
				{
					text: 'Confirm',
					handler: () => applyToggleAll(),
				},
			],
		})
	}

	const onDatePicked = (value: string | null | undefined) => {
		if (!value) {
			return
		}
		const dateId = value.split('T')[0]
		setSelectedDate(fromDateId(dateId))
		setIsDatePickerOpen(false)
	}

	const onConfirmTraining = () => {
		if (!canEdit) {
			return
		}
		setIsTrainingDayOverride(true)
		updateAttendance(localAttendance)
	}

	const onClearTraining = () => {
		if (!canEdit) {
			return
		}
		presentAlert({
			header: 'Remove training for this date?',
			message: 'This will delete all attendance data for this session.',
			buttons: [
				{
					text: 'Cancel',
					role: 'cancel',
				},
				{
					text: 'Delete',
					role: 'destructive',
					handler: async () => {
						await deleteTrainingSession(selectedDateId)
						setLocalAttendance({ })
						setIsTrainingDayOverride(false)
					},
				},
			],
		})
	}

	if (!canView) {
		return (
			<div className="private-season">
				🔒 This season is private.
			</div>
		)
	}

	if (noTeamSelected) {
		return <NoTeamSelected />
	}

	if (players === null || sessions === null || trainingNote === null) {
		return <LoadingSpinner />
	}

	if (!sortedPlayerIds.length) {
		return (
			<div className="roster-warning">
				No players have been added yet.
			</div>
		)
	}

	return (
		<section className="training">
			<IonSegment
				className="training-segment"
				value={segment}
				onIonChange={e => setSegment(e.detail.value as 'training' | 'notes')}
			>
				<IonSegmentButton value="training">
					Training
				</IonSegmentButton>
				<IonSegmentButton value="notes">
					<IonLabel>Notes</IonLabel>
				</IonSegmentButton>
			</IonSegment>

			{
				segment === 'training' && (
					<div className="training-date-bar">
						<IonButton
							fill="clear"
							disabled={!prevDateMs}
							onClick={() => prevDateMs && setSelectedDate(new Date(prevDateMs))}
						>
							<IonIcon icon={arrowBackSharp} />
						</IonButton>
						<div
							className="training-date-label"
							role="button"
							tabIndex={0}
							onClick={() => setIsDatePickerOpen(true)}
							onKeyDown={event => {
								if (event.key === 'Enter' || event.key === ' ') {
									event.preventDefault()
									setIsDatePickerOpen(true)
								}
							}}
							>
								{formatDateLabel(selectedDate)}
								{
									(todayStartMs !== undefined && toDayStartMs(selectedDate) === todayStartMs) ? (
										<div className="training-date-label-today">Today</div>
									) : (
										<TimeAgo
											date={selectedDate}
											now={() => todayStartMs}
										/>
									)
								}
							</div>
						<IonButton
							fill="clear"
							disabled={!nextDateMs}
							onClick={() => nextDateMs && setSelectedDate(new Date(nextDateMs))}
						>
							<IonIcon icon={arrowForwardSharp} />
						</IonButton>
					<IonButton
						id="training-date-trigger"
						fill="clear"
						onClick={() => setIsDatePickerOpen(true)}
					>
						<IonIcon icon={calendarSharp} />
					</IonButton>
					<IonModal
						className="training-date-modal"
						isOpen={isDatePickerOpen}
						keepContentsMounted={true}
						backdropDismiss={true}
						onDidDismiss={() => setIsDatePickerOpen(false)}
					>
						<IonDatetime
							id="training-date"
							presentation="date"
							value={selectedDateId}
							highlightedDates={highlightedDates}
							onIonChange={e => onDatePicked(e.detail.value as string)}
							showDefaultButtons={false}
							locale="en-US"
						/>
					</IonModal>
					</div>
				)
			}

				{
					segment === 'training' ? (
						<TrainingContent
							isTrainingDay={isTrainingDay}
							trainingQuestionPrefix={trainingQuestionPrefix}
							trainingQuestionDateLabel={trainingQuestionDateLabel}
							canEdit={canEdit}
							onConfirmTraining={onConfirmTraining}
							attendedIcons={attendedIcons}
							partIcons={partIcons}
							attendedCount={attendedCount}
							onToggleAll={onToggleAll}
							onClearTraining={onClearTraining}
							sortedPlayerIds={sortedPlayerIds}
							localAttendance={localAttendance}
							players={players}
							onCycleAttendance={onCycleAttendance}
						/>
					) : (
						<TrainingNotes
							canEdit={canEdit}
							noteDraft={noteDraft}
							noteCharLimit={NOTE_CHAR_LIMIT}
							onNoteInput={onNoteInput}
							onBlurSave={commitNoteSave}
						/>
					)
				}

			{
				segment === 'training' && (
					<div className="training-footer info">
						View training summary stats in
						{' '}
						<IonRouterLink
							className="training-summary-link"
							routerLink="/stats?mode=orange&orangeMode=training"
							routerDirection="root"
						>
							Stats <IonIcon icon={analyticsOutline} />
						</IonRouterLink>
					</div>
				)
			}

		</section>
	)
}

const toDateId = (date: Date) => {
	const year = date.getFullYear()
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const day = String(date.getDate()).padStart(2, '0')
	return `${year}-${month}-${day}`
}

const fromDateId = (dateId: string) => {
	const [ year, month, day ] = dateId.split('-').map(Number)
	return new Date(year, (month || 1) - 1, day || 1)
}

const toDayStartMs = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

const formatDateLabel = (date: Date) => {
	const label = date.toLocaleDateString('en-AU', {
		weekday: 'short',
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	})

	return label
}

export default Training

type TrainingContentProps = {
	isTrainingDay: boolean
	trainingQuestionPrefix: string
	trainingQuestionDateLabel: string
	canEdit: boolean
	onConfirmTraining: () => void
	attendedIcons: TrainingAttendanceState[]
	partIcons: TrainingAttendanceState[]
	attendedCount: number
	onToggleAll: () => void
	onClearTraining: () => void
	sortedPlayerIds: string[]
	localAttendance: Record<PlayerId, TrainingAttendanceState>
	players: Players | null
	onCycleAttendance: (playerId: PlayerId) => void
}

const TrainingContent = (props: TrainingContentProps) => {
	const {
		isTrainingDay,
		trainingQuestionPrefix,
		trainingQuestionDateLabel,
		canEdit,
		onConfirmTraining,
		attendedIcons,
		partIcons,
		attendedCount,
		onToggleAll,
		onClearTraining,
		sortedPlayerIds,
		localAttendance,
		players,
		onCycleAttendance,
	} = props

	if (!isTrainingDay) {
		return (
			<div className="training-title">
				<h1>
					<IonIcon icon={training} /> {trainingQuestionPrefix} training {trainingQuestionDateLabel}?
				</h1>
				<IonButton
					fill="solid"
					size="large"
					disabled={!canEdit}
					onClick={onConfirmTraining}
				>
					Yes!
				</IonButton>
			</div>
		)
	}

	return (
		<>
			<div className="training-summary">
				<div className="training-summary-left">
					<h2>
						<IonIcon icon={training} />
						Training Attendance:
					</h2>
					<div className="training-summary-counts">
						{
							attendedIcons.map((_, index) => (
								<IonIcon key={index} icon={happyOutline} className="training-summary-icon training-summary-icon-attended" />
							))
						}
						{
							partIcons.map((_, index) => (
								<IonIcon key={index} icon={timeOutline} className="training-summary-icon training-summary-icon-part" />
							))
						}
					</div>
				</div>
				<div className="training-summary-attended">
					<FadeText>
						{attendedCount}
					</FadeText>
				</div>
			</div>
			<div className="training-actions">
				<IonButton
					fill="clear"
					onClick={onToggleAll}
					disabled={!canEdit}
					size="small"
				>
					Toggle All
				</IonButton>
				<IonButton
					fill="clear"
					color="danger"
					size="small"
					onClick={onClearTraining}
					disabled={!canEdit}
				>
					Forget this day
				</IonButton>
			</div>

			<div className={`training-player-list ${canEdit ? '' : 'training-readonly'}`}>
				{
					sortedPlayerIds.map(playerId => {
						const state = localAttendance[playerId] || 'missed'
						const icon = TRAINING_STATE_ICONS[state]
						const label = TRAINING_STATE_LABELS[state]
						return (
							<IonButton
								key={playerId}
								fill="solid"
								strong={true}
								disabled={!canEdit}
								onClick={() => onCycleAttendance(playerId)}
								className={`training-player-button training-state-${state}`}
								aria-label={`${players?.[playerId]?.name || 'Player'}: ${label}`}
							>
								<PlayerName playerId={playerId} />
								<IonIcon icon={icon} className="training-state-icon" />
							</IonButton>
						)
					})
				}
			</div>

		</>
	)
}
